import { useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PDF_DEFAULT_SCALE } from '@/constants/pdf';
import type { PDFSearchResult, SearchResult } from './types';

interface PdfViewerStateArgs {
  initialPage: number;
  initialPageExplicit: boolean;
  preferredSearchPage?: number;
  highlightTerms: string[];
}

/**
 * Every hook below reads or writes this state, and several of them have to run
 * in a fixed effect order (load, render, highlight, search). Owning the state
 * here rather than inside whichever hook happens to write it first keeps that
 * order free to change without moving state between modules.
 */
export function usePdfViewerState({
  initialPage,
  initialPageExplicit,
  preferredSearchPage,
  highlightTerms,
}: PdfViewerStateArgs) {
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(PDF_DEFAULT_SCALE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(highlightTerms.join(' '));
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [pdfSearchResults, setPdfSearchResults] = useState<PDFSearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(highlightTerms.length > 0);
  const [showResultsSidebar, setShowResultsSidebar] = useState(false);
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const [pageRenderComplete, setPageRenderComplete] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const pageContainerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  /** Synchronous mirrors of the page sets: React state lands too late to stop
   * two pdf.js render tasks from sharing one canvas. */
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const renderingPagesRef = useRef<Set<number>>(new Set());
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const initialSearchPerformedRef = useRef(false);
  const lastHighlightTermsRef = useRef<string>('');
  const initialPageRef = useRef(initialPage);
  const initialPageExplicitRef = useRef(initialPageExplicit);
  const preferredSearchPageRef = useRef(preferredSearchPage);
  const pendingInitialScrollPageRef = useRef<number | null>(
    initialPage > 1 ? initialPage : null,
  );

  return {
    pdfDoc, setPdfDoc,
    currentPage, setCurrentPage,
    totalPages, setTotalPages,
    scale, setScale,
    loading, setLoading,
    error, setError,
    searchQuery, setSearchQuery,
    searchResults, setSearchResults,
    pdfSearchResults, setPdfSearchResults,
    currentResultIndex, setCurrentResultIndex,
    showSearchBar, setShowSearchBar,
    showResultsSidebar, setShowResultsSidebar,
    visiblePages, setVisiblePages,
    renderedPages, setRenderedPages,
    pageRenderComplete, setPageRenderComplete,
    containerRef,
    canvasRefs,
    highlightLayerRefs,
    pageContainerRefs,
    observerRef,
    renderedPagesRef,
    renderingPagesRef,
    pdfDocRef,
    loadingTaskRef,
    searchAbortControllerRef,
    initialSearchPerformedRef,
    lastHighlightTermsRef,
    initialPageRef,
    initialPageExplicitRef,
    preferredSearchPageRef,
    pendingInitialScrollPageRef,
  };
}

export type PdfViewerState = ReturnType<typeof usePdfViewerState>;
